import * as React from 'react'
import {
  User,
  Calendar as CalendarIcon,
  Maximize,
  Minimize,
  ChevronDown,
  Plus,
  Trash2,
} from 'lucide-react'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from './breadcrumb'
import { Button } from './button'
import { Calendar } from './calendar'
import { Card, CardContent, CardHeader, CardTitle } from './card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from './dialog'
import { Input } from './input'
import { Label } from './label'
import { Separator } from './separator'
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from './sidebar'
import { Badge } from './badge'
import { Sonner } from './toast'
import { Switch } from '../atomic/atoms/switch'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './popover'
import { cn } from '../lib/utils'

const providerNavData = {
  nav: [
    { name: "Info", icon: User },
    { name: "Schedule", icon: CalendarIcon },
  ],
}

export interface ProviderSchedule {
  id: string
  provider_id: string
  day_of_week: number // 0 = Sunday, 1 = Monday, etc.
  start_time: string
  end_time: string
  is_available: boolean
  treatment_types?: string[]
}

export interface ProviderAvailabilityOverride {
  id: string
  provider_id: string
  date: string
  start_time?: string
  end_time?: string
  available: boolean
  reason?: string
}

export interface ProviderInfo {
  id: string
  profile_id: string
  first_name: string
  last_name: string
  email: string
  phone?: string
  specialty?: string
  license_number?: string
  is_active?: boolean
  created_at: string
  treatment_types?: string[]
}

export interface AdminProviderInformationDialogProps {
  provider: ProviderInfo | null
  schedules?: ProviderSchedule[]
  overrides?: ProviderAvailabilityOverride[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaveSchedule?: (schedules: ProviderSchedule[]) => Promise<{ success: boolean; error?: string }>
  onAddOverride?: (override: Omit<ProviderAvailabilityOverride, 'id'>) => Promise<{ success: boolean; error?: string }>
  onDeleteOverride?: (overrideId: string) => Promise<{ success: boolean; error?: string }>
  initialSection?: string
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' },
]

export function AdminProviderInformationDialog({
  provider,
  schedules = [],
  overrides = [],
  open,
  onOpenChange,
  onSaveSchedule,
  onAddOverride,
  onDeleteOverride,
  initialSection = "Info"
}: AdminProviderInformationDialogProps) {
  const [isFullscreen, setIsFullscreen] = React.useState(true)
  const [activeSection, setActiveSection] = React.useState(initialSection)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false)

  // Schedule editing state
  const [editableSchedules, setEditableSchedules] = React.useState<ProviderSchedule[]>([])
  const [isSavingSchedule, setIsSavingSchedule] = React.useState(false)

  // Override form state
  const [newOverrideDate, setNewOverrideDate] = React.useState('')
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>()
  const [isDatePickerOpen, setIsDatePickerOpen] = React.useState(false)
  const [newOverrideAvailable, setNewOverrideAvailable] = React.useState(false)
  const [newOverrideStartTime, setNewOverrideStartTime] = React.useState('09:00')
  const [newOverrideEndTime, setNewOverrideEndTime] = React.useState('17:00')
  const [newOverrideReason, setNewOverrideReason] = React.useState('')
  const [isAddingOverride, setIsAddingOverride] = React.useState(false)

  // Initialize editable schedules when schedules prop changes
  React.useEffect(() => {
    setEditableSchedules(schedules)
  }, [schedules])

  if (!provider) {
    return null
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return "Not provided"
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const formatName = (first_name: string, last_name: string) => {
    return `${first_name} ${last_name}`.trim() || "Unknown Provider"
  }

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':')
    const hour = parseInt(hours, 10)
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    return `${displayHour}:${minutes} ${ampm}`
  }

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date)
    if (date) {
      // Format date as YYYY-MM-DD for the backend
      const formattedDate = date.toISOString().split('T')[0]
      setNewOverrideDate(formattedDate)
    } else {
      setNewOverrideDate('')
    }
    setIsDatePickerOpen(false)
  }

  const handleScheduleChange = (dayOfWeek: number, field: 'start_time' | 'end_time' | 'is_available', value: string | boolean) => {
    setEditableSchedules(prev => {
      const existing = prev.find(s => s.day_of_week === dayOfWeek)
      if (existing) {
        return prev.map(s =>
          s.day_of_week === dayOfWeek
            ? { ...s, [field]: value }
            : s
        )
      } else {
        // Create new schedule for this day
        return [...prev, {
          id: `new-${dayOfWeek}`,
          provider_id: provider.id,
          day_of_week: dayOfWeek,
          start_time: field === 'start_time' ? value as string : '09:00:00',
          end_time: field === 'end_time' ? value as string : '17:00:00',
          is_available: field === 'is_available' ? value as boolean : true,
        }]
      }
    })
  }

  const handleSaveSchedule = async () => {
    if (!onSaveSchedule) return

    setIsSavingSchedule(true)
    try {
      const result = await onSaveSchedule(editableSchedules)
      if (result.success) {
        Sonner.saved('Provider schedule')
      } else {
        Sonner.error('Provider schedule', result.error || 'Failed to save')
      }
    } catch (error) {
      Sonner.error('Provider schedule', 'An unexpected error occurred')
    } finally {
      setIsSavingSchedule(false)
    }
  }

  const handleAddOverride = async () => {
    if (!onAddOverride || !newOverrideDate) return

    setIsAddingOverride(true)
    try {
      const override: Omit<ProviderAvailabilityOverride, 'id'> = {
        provider_id: provider.id,
        date: newOverrideDate,
        available: newOverrideAvailable,
        start_time: newOverrideAvailable ? newOverrideStartTime : undefined,
        end_time: newOverrideAvailable ? newOverrideEndTime : undefined,
        reason: newOverrideReason || undefined,
      }

      const result = await onAddOverride(override)
      if (result.success) {
        Sonner.scheduleOverrideSaved(newOverrideDate)
        // Reset form
        setNewOverrideDate('')
        setSelectedDate(undefined)
        setNewOverrideAvailable(false)
        setNewOverrideStartTime('09:00')
        setNewOverrideEndTime('17:00')
        setNewOverrideReason('')
      } else {
        Sonner.error('Availability override', result.error || 'Failed to add')
      }
    } catch (error) {
      Sonner.error('Availability override', 'An unexpected error occurred')
    } finally {
      setIsAddingOverride(false)
    }
  }

  const handleDeleteOverride = async (overrideId: string) => {
    if (!onDeleteOverride) return

    try {
      const result = await onDeleteOverride(overrideId)
      if (result.success) {
        Sonner.saved('Override removed')
      } else {
        Sonner.error('Override', result.error || 'Failed to delete')
      }
    } catch (error) {
      Sonner.error('Override', 'An unexpected error occurred')
    }
  }

  const getCurrentBreadcrumb = () => {
    return (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem className="hidden md:block">
            <BreadcrumbLink href="#">Provider Information</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator className="hidden md:block" />
          <BreadcrumbItem>
            <BreadcrumbPage>{activeSection}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    )
  }

  const renderContent = () => {
    if (activeSection === "Schedule") {
      return (
        <div className="space-y-6">
          {/* Weekly Schedule */}
          <Card>
            <CardHeader>
              <CardTitle>Weekly Schedule</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {DAYS_OF_WEEK.map((day) => {
                  const schedule = editableSchedules.find(s => s.day_of_week === day.value)
                  const isAvailable = schedule?.is_available !== false

                  return (
                    <div key={day.value} className="flex items-center gap-4 py-2 border-b last:border-b-0">
                      <div className="w-24 font-medium">{day.label}</div>

                      <Switch
                        checked={isAvailable}
                        onCheckedChange={(checked) => handleScheduleChange(day.value, 'is_available', checked)}
                      />

                      {isAvailable && (
                        <>
                          <div className="flex items-center gap-2">
                            <Label className="text-sm text-muted-foreground">From</Label>
                            <Input
                              type="time"
                              value={schedule?.start_time?.slice(0, 5) || '09:00'}
                              onChange={(e) => handleScheduleChange(day.value, 'start_time', e.target.value + ':00')}
                              className="w-32"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <Label className="text-sm text-muted-foreground">To</Label>
                            <Input
                              type="time"
                              value={schedule?.end_time?.slice(0, 5) || '17:00'}
                              onChange={(e) => handleScheduleChange(day.value, 'end_time', e.target.value + ':00')}
                              className="w-32"
                            />
                          </div>
                        </>
                      )}

                      {!isAvailable && (
                        <span className="text-sm text-muted-foreground">Not available</span>
                      )}
                    </div>
                  )
                })}
              </div>

              {onSaveSchedule && (
                <div className="mt-6 flex justify-end">
                  <Button onClick={handleSaveSchedule} disabled={isSavingSchedule}>
                    {isSavingSchedule ? 'Saving...' : 'Save Schedule'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Availability Overrides */}
          <Card>
            <CardHeader>
              <CardTitle>Availability Overrides</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Add specific dates when the provider's availability differs from their regular schedule.
              </p>

              {/* Add Override Form */}
              {onAddOverride && (
                <div className="bg-muted/50 rounded-lg p-4 mb-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date</Label>
                      <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !selectedDate && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {selectedDate ? formatDate(selectedDate.toISOString()) : "Select date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={selectedDate}
                            onSelect={handleDateSelect}
                            disabled={(date) => date < new Date()}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label>Reason (optional)</Label>
                      <Input
                        placeholder="e.g., Vacation, Conference"
                        value={newOverrideReason}
                        onChange={(e) => setNewOverrideReason(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-4">
                      <Switch
                        checked={newOverrideAvailable}
                        onCheckedChange={setNewOverrideAvailable}
                      />
                      <div className="space-y-1">
                        <span className="text-sm font-medium">
                          {newOverrideAvailable ? 'Available with custom hours' : 'Not available (day off)'}
                        </span>
                        <p className="text-xs text-muted-foreground">
                          {newOverrideAvailable 
                            ? 'Provider will be available during specified times only'
                            : 'Provider will be completely unavailable on this date'
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  {newOverrideAvailable && (
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Label className="text-sm">From</Label>
                        <Input
                          type="time"
                          value={newOverrideStartTime}
                          onChange={(e) => setNewOverrideStartTime(e.target.value)}
                          className="w-32"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-sm">To</Label>
                        <Input
                          type="time"
                          value={newOverrideEndTime}
                          onChange={(e) => setNewOverrideEndTime(e.target.value)}
                          className="w-32"
                        />
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={handleAddOverride}
                    disabled={!newOverrideDate || isAddingOverride}
                    size="sm"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {isAddingOverride ? 'Adding...' : 'Add Override'}
                  </Button>
                </div>
              )}

              {/* Existing Overrides */}
              {overrides.length > 0 ? (
                <div className="space-y-2">
                  {overrides.map((override) => (
                    <div
                      key={override.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div>
                          <p className="font-medium">{formatDate(override.date)}</p>
                          {override.reason && (
                            <p className="text-sm text-muted-foreground">{override.reason}</p>
                          )}
                        </div>
                        <Badge variant={override.available ? 'default' : 'secondary'}>
                          {override.available
                            ? `${formatTime(override.start_time || '09:00')} - ${formatTime(override.end_time || '17:00')}`
                            : 'Not Available'
                          }
                        </Badge>
                      </div>
                      {onDeleteOverride && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteOverride(override.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No availability overrides set.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )
    }

    // Info section (default)
    return (
      <div className="space-y-4">
        <div className="bg-muted/50 rounded-xl p-4">
          <h3 className="font-semibold mb-2">Basic Information</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Name:</span>
              <p>{formatName(provider.first_name, provider.last_name)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Email:</span>
              <p>{provider.email}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Phone:</span>
              <p>{provider.phone || "Not provided"}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Provider ID:</span>
              <p className="font-mono">{provider.id.slice(0, 8).toUpperCase()}</p>
            </div>
          </div>
        </div>

        <div className="bg-muted/50 rounded-xl p-4">
          <h3 className="font-semibold mb-2">Professional Information</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Specialty:</span>
              <p className="capitalize">{provider.specialty?.replace('_', ' ') || "Not specified"}</p>
            </div>
            <div>
              <span className="text-muted-foreground">License Number:</span>
              <p>{provider.license_number || "Not provided"}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Status:</span>
              <Badge variant={provider.is_active !== false ? 'default' : 'secondary'}>
                {provider.is_active !== false ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            <div>
              <span className="text-muted-foreground">Member Since:</span>
              <p>{formatDate(provider.created_at)}</p>
            </div>
          </div>
        </div>

        {provider.treatment_types && provider.treatment_types.length > 0 && (
          <div className="bg-muted/50 rounded-xl p-4">
            <h3 className="font-semibold mb-2">Treatment Types</h3>
            <div className="flex flex-wrap gap-2">
              {provider.treatment_types.map((type, index) => (
                <Badge key={index} variant="outline" className="capitalize">
                  {type.replace('_', ' ')}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "overflow-hidden p-0 bg-card",
          isFullscreen
            ? "!fixed !inset-0 !w-screen !h-screen !max-w-none !max-h-none !rounded-none !border-0 !left-0 !top-0 !transform-none"
            : "max-h-[80vh] md:max-h-[600px] w-[calc(100vw-32px)] max-w-[calc(100vw-32px)] sm:!w-[90vw] sm:!max-w-[900px] lg:!max-w-[1000px]"
        )}
      >
        <DialogTitle className="sr-only">
          Provider Information - {formatName(provider.first_name, provider.last_name)}
        </DialogTitle>
        <DialogDescription className="sr-only">
          View and manage provider information and schedule.
        </DialogDescription>
        <SidebarProvider className="items-start flex-1 flex">
          <Sidebar
            collapsible="none"
            className="hidden md:flex w-48 border-r border-sidebar-border dark:border-transparent"
          >
            <SidebarContent>
              <SidebarGroup>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {providerNavData.nav.map((item) => (
                      <SidebarMenuItem key={item.name}>
                        <SidebarMenuButton
                          asChild
                          isActive={item.name === activeSection}
                        >
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault()
                              setActiveSection(item.name)
                            }}
                          >
                            <item.icon />
                            <span>{item.name}</span>
                          </a>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </SidebarContent>
          </Sidebar>
          <main
            className="flex flex-1 flex-col overflow-hidden bg-card h-full min-h-0"
          >
            <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
              <div className="flex items-center gap-2 px-4 w-full">
                {/* Mobile Navigation Dropdown */}
                <div className="flex md:hidden mr-2">
                  <Popover open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        className="w-auto justify-between border border-border hover:bg-muted px-3 h-8"
                      >
                        <span className="truncate">{activeSection}</span>
                        <ChevronDown className="h-4 w-4 ml-1 shrink-0" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-48 p-2" align="start">
                      <div className="space-y-1">
                        {providerNavData.nav.map((item) => (
                          <Button
                            key={item.name}
                            variant={item.name === activeSection ? "secondary" : "ghost"}
                            size="sm"
                            className="w-full justify-start"
                            onClick={() => {
                              setActiveSection(item.name)
                              setIsMobileMenuOpen(false)
                            }}
                          >
                            <item.icon className="mr-2 h-4 w-4" />
                            {item.name}
                          </Button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 hidden md:block"
                  onClick={() => setIsFullscreen(!isFullscreen)}
                >
                  {isFullscreen ? (
                    <Minimize className="h-4 w-4" />
                  ) : (
                    <Maximize className="h-4 w-4" />
                  )}
                </Button>
                {getCurrentBreadcrumb()}
              </div>
            </header>
            <div className="flex flex-col gap-4 p-4 pt-0 flex-1 overflow-y-auto">
              {renderContent()}
            </div>
          </main>
        </SidebarProvider>
      </DialogContent>
    </Dialog>
  )
}
